/**
 * Mapping of standard workspace names to their document cards
 * Each card has: section code and card name (information_name)
 */
export interface DocumentCard {
  sectionCode: string;
  informationName: string;
}

/** Must match engagement FRF string for Indian Accounting Standards (Ind AS) */
export const FRF_IND_AS_DOCUMENT_CARDS = "Indian Accounting Standards (Ind AS)";

/**
 * Document/IPE-IUC cards for a workspace, adjusted for FRF (Ind AS vs AS).
 * Ind AS: advance/capital listing inputs use Execution_OTAS_* under Other Assets and are omitted from Other Receivables.
 */
export function getWorkspaceDocumentCards(
  workspaceName: string,
  frf?: string | null,
): DocumentCard[] {
  const base = WORKSPACE_DOCUMENT_CARDS[workspaceName] || [];
  if (frf === FRF_IND_AS_DOCUMENT_CARDS) {
    if (workspaceName === "Other Assets") {
      const extras: DocumentCard[] = [
        {
          sectionCode: "Execution_OTAS_ATS",
          informationName: "Advance to supplier",
        },
        {
          sectionCode: "Execution_OTAS_CAL",
          informationName: "Capital advance listing",
        },
      ];
      const have = new Set(base.map((c) => c.sectionCode));
      return [...base, ...extras.filter((c) => !have.has(c.sectionCode))];
    }
    if (workspaceName === "Other Receivables") {
      return base.filter(
        (c) =>
          c.sectionCode !== "Execution_OTR_ATS" &&
          c.sectionCode !== "Execution_OTR_CAL",
      );
    }
  }
  return [...base];
}

export const WORKSPACE_DOCUMENT_CARDS: Record<string, DocumentCard[]> = {
  "Employee Benefits Expense": [
    { sectionCode: "Execution_EBE_PayRegistrar", informationName: "Pay Register" },
    { sectionCode: "Execution_EBE_Actuary", informationName: "Actuary Testing" },
    { sectionCode: "Execution_EBE_AL", informationName: "Employee Additions" },
    { sectionCode: "Execution_EBE_CTC", informationName: "CTC Report" },
    { sectionCode: "Execution_EBE_DL", informationName: "Employee Deletions" },
  ],
  "Property, Plant & Equipment": [
    { sectionCode: "Execution_PPE_AL", informationName: "PPE Acquisitions" },
    { sectionCode: "Execution_PPE_CWIP", informationName: "CWIP Listing" },
    { sectionCode: "Execution_PPE_DL", informationName: "PPE Disposals" },
    { sectionCode: "Execution_PPE_FAR", informationName: "FAR File" },
    { sectionCode: "Execution_PPE_FARPrev", informationName: "Prev FAR File" },
  ],
  "Cost of Materials Consumed": [
    { sectionCode: "Execution_COGS_PurchaseRegister", informationName: "Purchase Register" },
  ],
  "Inventory": [
    { sectionCode: "Execution_INV_IR", informationName: "Inventory Register" },
    { sectionCode: "Execution_INV_MCR", informationName: "Management Count Report" },
    { sectionCode: "Execution_INV_SSR", informationName: "Subsequent Sales Register" },
  ],
  "Intangible Assets": [
    { sectionCode: "Execution_IT_IntAR", informationName: "Intangible Assets Register" },
    { sectionCode: "Execution_IT_AL", informationName: "Intangible Asset Acquisitions" },
    { sectionCode: "Execution_IT_DL", informationName: "Intangible Asset Disposals" },
    { sectionCode: "Execution_IT_PrevIntAR", informationName: "Previous year Intangible Assets Register" },
  ],
  "Trade Payables": [
    { sectionCode: "Execution_TP_TPR", informationName: "Trade Payable Register" },
    { sectionCode: "Execution_TP_VM", informationName: "Vendor Master" },
  ],
  "Trade Receivables": [
    { sectionCode: "Execution_TR_TRR", informationName: "Trade Receivable Register" },
    { sectionCode: "Execution_TR_CM", informationName: "Customer Master" },
  ],
  "Other Receivables": [
    { sectionCode: "Execution_OTR_ATS", informationName: "Advance to supplier" },
    { sectionCode: "Execution_OTR_CAL", informationName: "Capital advance listing" },
  ],
  "Other Assets": [
    { sectionCode: "Execution_OTAS_PrepExpLis", informationName: "Prepaid Expense Listing" },
  ],
  "Other Financial Assets": [],
  "Other Liabilities": [
    { sectionCode: "Execution_OTL_AFC", informationName: "Advance from customer" },
    { sectionCode: "Execution_OTL_PTP", informationName: "Payables to PPE" },
  ],
  "Other Financial Liabilities": [
    { sectionCode: "Execution_OFL_PTP", informationName: "Payables to PPE" },
  ],
};

/**
 * Get workspace prefix from workspace name
 * This maps workspace names to their prefixes used in section codes
 */
export function getWorkspacePrefix(workspaceName: string): string | null {
  const prefixMap: Record<string, string> = {
    "Employee Benefits Expense": "EBE",
    "Property, Plant & Equipment": "PPE",
    "Cost of Materials Consumed": "COGS",
    "Inventory": "INV",
    "Intangible Assets": "IT",
    "Trade Payables": "TP",
    "Trade Receivables": "TR",
    "Other Receivables": "OTR",
    "Other Assets": "OTAS",
    "Other Financial Assets": "OFA",
    "Other Liabilities": "OTL",
    "Other Financial Liabilities": "OFL",
  };
  return prefixMap[workspaceName] || null;
}
